import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjsTZ from "../../../../utils/dayjs";
import { API } from "../../../../helpers/API/amplifyApi";
import { useAuth } from "../../../../authentication/authHelpers";
import { canAccessSecurityConsole } from "../../../../utils/securityScope";
import "./sessionManager.scss";

const STATUS_COLOR = {
  online: "green",
  idle: "gold",
  offline: "default",
};

function SessionManager() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { isDarkMode } = useSelector((state) => state.usersList);
  const allowed = canAccessSecurityConsole(auth);
  const darkPopup = isDarkMode
    ? { popup: { root: "sessionManagerDarkDropdown" } }
    : undefined;

  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [kicking, setKicking] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [mfaInfo, setMfaInfo] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("security", "/security/sessions");
      if (res?.status === false) {
        message.error(res?.error || "Nuk u ngarkuan sesionet");
        setSessions([]);
      } else {
        setSessions(Array.isArray(res?.sessions) ? res.sessions : []);
      }
    } catch (err) {
      console.error(err);
      message.error("Nuk u ngarkuan sesionet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    loadSessions();
    const t = setInterval(loadSessions, 30000);
    return () => clearInterval(t);
  }, [allowed, loadSessions]);

  const userOptions = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      if (!s.identityId || map.has(s.identityId)) continue;
      map.set(s.identityId, {
        value: s.identityId,
        label: `${s.employeeName || s.email} (${s.email || "—"})`,
        raw: s,
      });
    }
    return [...map.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), "sq")
    );
  }, [sessions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${s.employeeName || ""} ${s.email || ""} ${s.ipv4 || ""} ${
        s.localIp || ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [sessions, statusFilter, search]);

  const onKick = async (row) => {
    const key = `${row.identityId}:${row.sessionId || "all"}`;
    setKicking(key);
    try {
      const res = await API.post("security", "/security/sessions/kick", {
        body: {
          identityId: row.identityId,
          sessionId: row.sessionId || undefined,
        },
      });
      if (res?.status === false) {
        message.error(res?.error || "Kick dështoi");
      } else {
        message.success("Përdoruesi u nxorr nga sesioni");
        await loadSessions();
      }
    } catch (err) {
      console.error(err);
      message.error("Kick dështoi");
    } finally {
      setKicking(null);
    }
  };

  const loadDevices = async (identityId) => {
    if (!identityId) {
      setDevices([]);
      return;
    }
    setDevicesLoading(true);
    try {
      const res = await API.get(
        "security",
        `/security/cognito-devices/${encodeURIComponent(identityId)}`
      );
      setDevices(Array.isArray(res?.devices) ? res.devices : []);
      if (res?.status === false && res?.error) {
        message.warning(res.error);
      }
    } catch (err) {
      console.error(err);
      message.error("Nuk u ngarkuan pajisjet Cognito");
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const loadMfa = async (identityId) => {
    if (!identityId) {
      setMfaInfo(null);
      return;
    }
    setMfaLoading(true);
    try {
      const res = await API.get(
        "security",
        `/security/mfa-status/${encodeURIComponent(identityId)}`
      );
      setMfaInfo(res);
    } catch (err) {
      console.error(err);
      message.error("Nuk u ngarkua statusi MFA");
      setMfaInfo(null);
    } finally {
      setMfaLoading(false);
    }
  };

  const onSelectUser = (identityId) => {
    setSelectedUser(identityId);
    loadDevices(identityId);
    loadMfa(identityId);
  };

  const onForgetDevice = async (deviceKey) => {
    if (!selectedUser || !deviceKey) return;
    try {
      const res = await API.del(
        "security",
        `/security/cognito-devices/${encodeURIComponent(
          selectedUser
        )}/${encodeURIComponent(deviceKey)}`
      );
      if (res?.status === false) {
        message.error(res?.error || "Fshirja dështoi");
      } else {
        message.success("Pajisja u hoq");
        await loadDevices(selectedUser);
      }
    } catch (err) {
      console.error(err);
      message.error("Fshirja dështoi");
    }
  };

  const onPolicyChange = async (identityId, allowedDeviceTypes) => {
    setPolicySaving(true);
    try {
      const res = await API.put(
        "security",
        `/security/users/${encodeURIComponent(identityId)}/device-policy`,
        { body: { allowedDeviceTypes } }
      );
      if (res?.status === false) {
        message.error(res?.error || "Ruajtja dështoi");
      } else {
        message.success("Politika e pajisjes u ruajt");
        await loadSessions();
      }
    } catch (err) {
      console.error(err);
      message.error("Ruajtja dështoi");
    } finally {
      setPolicySaving(false);
    }
  };

  if (!allowed) {
    return <Navigate to="/konfigurimet" replace />;
  }

  const sessionColumns = [
    {
      title: "Punonjësi",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (v, r) => (
        <div>
          <div>{v}</div>
          <div className="sessionManager__muted">{r.email}</div>
        </div>
      ),
    },
    {
      title: "Statusi",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag>,
    },
    {
      title: "IP",
      key: "ip",
      width: 160,
      render: (_, r) => (
        <div>
          <div>{r.ipv4 || "—"}</div>
          <div className="sessionManager__muted">LAN: {r.localIp || "—"}</div>
        </div>
      ),
    },
    {
      title: "Pajisja",
      key: "device",
      width: 180,
      render: (_, r) => (
        <div>
          <div>{r.deviceType || "—"}</div>
          <div className="sessionManager__muted">
            {[r.osName, r.browserName].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Parë së fundi",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      width: 150,
      render: (v) => (v ? dayjsTZ(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
    {
      title: "Politika",
      dataIndex: "allowedDeviceTypes",
      key: "allowedDeviceTypes",
      width: 140,
      render: (v, r) => (
        <Select
          size="small"
          value={v || "both"}
          disabled={policySaving}
          style={{ width: 120 }}
          classNames={darkPopup}
          onChange={(val) => onPolicyChange(r.identityId, val)}
          options={[
            { value: "both", label: "Të dyja" },
            { value: "desktop", label: "Desktop" },
            { value: "mobile", label: "Mobile" },
          ]}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, r) => (
        <Popconfirm
          title="Nxirr përdoruesin nga sesioni?"
          okText="Po"
          cancelText="Jo"
          onConfirm={() => onKick(r)}
        >
          <Button
            danger
            size="small"
            loading={kicking === `${r.identityId}:${r.sessionId || "all"}`}
          >
            Kick
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const policyRows = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      if (!map.has(s.identityId)) {
        map.set(s.identityId, {
          identityId: s.identityId,
          employeeName: s.employeeName,
          email: s.email,
          allowedDeviceTypes: s.allowedDeviceTypes || "both",
          auth2FaActive: s.auth2FaActive,
        });
      }
    }
    return [...map.values()];
  }, [sessions]);

  return (
    <div
      className={`sessionManager ${isDarkMode ? "sessionManager--dark" : ""}`}
    >
      <header className="sessionManager__header">
        <div>
          <h1>Menaxheri i sesioneve</h1>
          <p>Vetëm për llogaritë e autorizuara · info@flex.al</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadSessions} loading={loading}>
            Rifresko
          </Button>
        </Space>
      </header>

      <Tabs
        items={[
          {
            key: "live",
            label: "Sesione live",
            children: (
              <>
                <Space wrap className="sessionManager__toolbar">
                  <Input.Search
                    allowClear
                    placeholder="Kërko emër, email, IP…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 280 }}
                  />
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 160 }}
                    classNames={darkPopup}
                    options={[
                      { value: "all", label: "Të gjitha" },
                      { value: "online", label: "Online" },
                      { value: "idle", label: "Idle" },
                      { value: "offline", label: "Offline" },
                    ]}
                  />
                  <Tag>
                    {filtered.length} / {sessions.length}
                  </Tag>
                </Space>
                <Table
                  rowKey={(r) =>
                    `${r.identityId}-${r.sessionId || "ws"}-${r.lastSeenAt}`
                  }
                  loading={loading}
                  columns={sessionColumns}
                  dataSource={filtered}
                  pagination={{ pageSize: 25, showSizeChanger: true }}
                  size="middle"
                  scroll={{ x: 960 }}
                />
              </>
            ),
          },
          {
            key: "policy",
            label: "Politika pajisjesh",
            children: (
              <Table
                rowKey="identityId"
                dataSource={policyRows}
                pagination={{ pageSize: 25 }}
                columns={[
                  { title: "Punonjësi", dataIndex: "employeeName" },
                  { title: "Email", dataIndex: "email" },
                  {
                    title: "Aksesi",
                    dataIndex: "allowedDeviceTypes",
                    render: (v, r) => (
                      <Select
                        value={v || "both"}
                        style={{ width: 160 }}
                        disabled={policySaving}
                        classNames={darkPopup}
                        onChange={(val) => onPolicyChange(r.identityId, val)}
                        options={[
                          { value: "both", label: "Desktop + Mobile" },
                          { value: "desktop", label: "Vetëm desktop" },
                          { value: "mobile", label: "Vetëm mobile" },
                        ]}
                      />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: "cognito",
            label: "Pajisje Cognito",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Zgjidh përdoruesin"
                  style={{ minWidth: 360 }}
                  options={userOptions}
                  value={selectedUser}
                  onChange={onSelectUser}
                  classNames={darkPopup}
                />
                <Table
                  rowKey={(r) => r.DeviceKey || r.DeviceCreateDate}
                  loading={devicesLoading}
                  dataSource={devices}
                  locale={{ emptyText: "Nuk ka pajisje të kujtuara" }}
                  columns={[
                    {
                      title: "Device Key",
                      dataIndex: "DeviceKey",
                      ellipsis: true,
                    },
                    {
                      title: "Krijuar",
                      dataIndex: "DeviceCreateDate",
                      render: (v) =>
                        v ? dayjsTZ(v).format("DD/MM/YYYY HH:mm") : "—",
                    },
                    {
                      title: "Parë së fundi",
                      dataIndex: "DeviceLastAuthenticatedDate",
                      render: (v) =>
                        v ? dayjsTZ(v).format("DD/MM/YYYY HH:mm") : "—",
                    },
                    {
                      title: "",
                      key: "forget",
                      render: (_, r) => (
                        <Popconfirm
                          title="Harro këtë pajisje?"
                          onConfirm={() => onForgetDevice(r.DeviceKey)}
                        >
                          <Button danger size="small">
                            Harro
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Space>
            ),
          },
          {
            key: "mfa",
            label: "MFA",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Zgjidh përdoruesin"
                  style={{ minWidth: 360 }}
                  options={userOptions}
                  value={selectedUser}
                  onChange={onSelectUser}
                  classNames={darkPopup}
                />
                {mfaLoading ? (
                  <div>Duke ngarkuar…</div>
                ) : mfaInfo ? (
                  <div className="sessionManager__mfaCard">
                    <p>
                      <strong>Email OTP (app):</strong>{" "}
                      {mfaInfo.emailOtpActive ? (
                        <Tag color="green">Aktiv</Tag>
                      ) : (
                        <Tag>Jo aktiv</Tag>
                      )}
                    </p>
                    <p>
                      <strong>Cognito preferred MFA:</strong>{" "}
                      {mfaInfo.cognito?.preferredMfa || "—"}
                    </p>
                    <p>
                      <strong>Cognito MFA options:</strong>{" "}
                      {(mfaInfo.cognito?.userMfaOptions || []).join(", ") ||
                        "—"}
                    </p>
                    <p>
                      <strong>User status:</strong>{" "}
                      {mfaInfo.cognito?.userStatus || "—"}
                    </p>
                    {selectedUser ? (
                      <Button
                        type="link"
                        onClick={() =>
                          navigate(`/konfigurimet/perdoruesit/${selectedUser}`)
                        }
                      >
                        Hap cilësimet e përdoruesit →
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="sessionManager__muted">
                    Zgjidh një përdorues për të parë statusin MFA.
                  </div>
                )}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}

export default SessionManager;
